import { DEFAULT_HTML2CANVAS_PNG_OPTIONS } from './downloadDomElementAsPng';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
/** Minimum content height (mm) to justify a new page; avoids a sliver on an extra page */
const MIN_PAGE_CONTENT_MM = 5;
const JPEG_QUALITY = 0.9;

const PDF_HTML2CANVAS_OPTIONS = {
  ...DEFAULT_HTML2CANVAS_PNG_OPTIONS,
} as const;

interface PdfDoc {
  addPage(): void;
  addImage(imgData: string, format: string, x: number, y: number, w: number, h: number): void;
}

/**
 * Places one section's image on the PDF, continuing from `cursorMm` on the current page when
 * it fits there, or starting a fresh page when it doesn't (or `forceNewPage` says to) - so a
 * page break only ever falls between sections, never through the middle of one. A section
 * taller than a full page is the exception: it always starts at the top of a fresh page and is
 * sliced across as many pages as it needs, since there's no way to fit it without slicing.
 * Returns the cursor position (mm from the page top) after this section, for the next call.
 */
function placeSectionOnPdf(
  pdf: PdfDoc,
  imgData: string,
  imgWidthMm: number,
  imgHeightMm: number,
  cursorMm: number,
  forceNewPage: boolean,
  isFirstSection: boolean,
): number {
  const fitsInRemainingSpace = imgHeightMm <= A4_HEIGHT_MM - cursorMm;
  const startsNewPage = !isFirstSection && (forceNewPage || !fitsInRemainingSpace);
  if (startsNewPage) {
    pdf.addPage();
  }
  const pageTopCursorMm = startsNewPage ? 0 : cursorMm;

  if (imgHeightMm <= A4_HEIGHT_MM - pageTopCursorMm) {
    pdf.addImage(imgData, 'JPEG', 0, pageTopCursorMm, imgWidthMm, imgHeightMm);
    return pageTopCursorMm + imgHeightMm;
  }

  // Taller than a full page even from a fresh top: slice it across as many pages as it needs.
  let yOffset = 0;
  let lastPageContentMm = 0;
  while (yOffset < imgHeightMm) {
    if (yOffset > 0) {
      const remainingMm = imgHeightMm - yOffset;
      if (remainingMm < MIN_PAGE_CONTENT_MM) {
        break;
      }
      pdf.addPage();
    }
    pdf.addImage(imgData, 'JPEG', 0, -yOffset, imgWidthMm, imgHeightMm);
    lastPageContentMm = Math.min(A4_HEIGHT_MM, imgHeightMm - yOffset);
    yOffset += A4_HEIGHT_MM;
  }
  return lastPageContentMm;
}

export async function exportToPdf(reportElement: HTMLElement, filename: string): Promise<void> {
  const { default: html2canvas } = await import('html2canvas');
  const { default: jsPDF } = await import('jspdf');

  const root = reportElement.firstElementChild ?? reportElement;
  const sections = root.querySelectorAll<HTMLElement>('[data-pdf-section]');

  const pdf = new jsPDF('p', 'mm', 'a4');
  const imgWidthMm = A4_WIDTH_MM;

  if (sections.length > 0) {
    let cursorMm = 0;
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const canvas = await html2canvas(section, PDF_HTML2CANVAS_OPTIONS);
      const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;
      const forceNewPage = section.hasAttribute('data-pdf-page-break-before');

      cursorMm = placeSectionOnPdf(pdf, imgData, imgWidthMm, imgHeightMm, cursorMm, forceNewPage, i === 0);
    }
  } else {
    const canvas = await html2canvas(reportElement, PDF_HTML2CANVAS_OPTIONS);
    const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;
    placeSectionOnPdf(pdf, imgData, imgWidthMm, imgHeightMm, 0, false, true);
  }

  pdf.save(filename);
}
